import _ from 'lodash';
import config from 'client/shared/config';
import moment from 'moment';
import { observable, action, computed } from 'mobx';

import artistStore from './artist-store';


export default class Song {
	_id = null;

	store = null;

	constructor (store, data) {
		this.store = store;

		if (_.isObject(data)) {

			this.update(data);
		}
	}

	/**
	 * Observables & Computeds Properties
	 */

 	@computed
 	get id () {
 		return this._id;
 	}


	@observable
	_artistID = '';

	@computed
	get artistID () {
		return this._artistID;
	}

	set artistID (artistID) {
		this._artistID = artistID;
	}

	@computed 
	get artist() {
		const artist = artistStore.getArtistByID(this._artistID);
		return artist?.name || '';
	}

	@observable
	_additionalArtists = '';

	@computed
	get additionalArtists () {
		return this._additionalArtists;
	}

	set additionalArtists (additionalArtists) {
		this._additionalArtists = additionalArtists;
	}


	@observable
	_title = '';

	@computed
	get title () {
		return this._title;
	}

	set title (title) {
		this._title = title;
	}


	@observable
	_publisher = '';

	@computed
	get publisher () {
		return this._publisher;
	}

	set publisher (publisher) {
		this._publisher = publisher;
	}


	@observable
	_songWriters = '';

	@computed
	get songWriters () {
		return this._songWriters;
	}

	set songWriters (songWriters) {
		this._songWriters = songWriters;
	}


	@observable
	_ISRC = '';

	@computed
	get ISRC () {
		return this._ISRC;
	}

	set ISRC (ISRC) {
		this._ISRC = ISRC;
	}


	/**
	 * _streamFile is the HLS manifest file
	 * For Apple devices, the HLS manifest file is used
	 */
	@observable
	_streamFile = '';

	@computed
	get streamFile () {
		return this._streamFile;
	}

	set streamFile (streamFile) {
		this._streamFile = streamFile;
	}

	/**
	 * _dashManifest is the DASH manifest file
	 * For non-Apple devices, the DASH manifest file is used
	 */
	@observable	
	_dashManifest = '';

	@computed
	get dashManifest () {
		return this._dashManifest;
	}

	set dashManifest (dashManifest) {
		this._dashManifest = dashManifest;
	}

	/**
	 * streamingUrl is a computed property that returns the correct URL based on the user's browser
	 */
	@computed
	get streamingUrl () {
		const isApple = /iPad|iPhone|iPod|Mac/.test(navigator.userAgent);
		return `${config.paths.songBasePath}/${isApple ? this._streamFile : this._dashManifest}`;
	}

	/**
	 * _previewFile is the URL to a preview mp3 file
	 */
	@observable
	_previewFile = '';

	@computed
	get previewFile () {
		return this._previewFile;
	}

	set previewFile (previewFile) {
		this._previewFile = previewFile;
	}

	/**
	 * _downloadFile is the URL to the raw mp3 file
	 */
	@observable
	_downloadFile = '';

	@computed
	get downloadFile () {
		return this._downloadFile;
	}

	set downloadFile (downloadFile) {
		this._downloadFile = downloadFile;
	}


	@observable
	_embedCode = '';

	@computed
	get embedCode () {
		return this._embedCode;
	}

	set embedCode (embedCode) {
		this._embedCode = embedCode;
	}


	@observable
	_explicit = false;

	@computed
	get explicit () {
		return this._explicit;
	}

	@observable
	_releaseDate = '';

	@computed
	get releaseDate () {
		return this._releaseDate;
	}

	set releaseDate (releaseDate) {
		this._releaseDate = releaseDate;
	}

	@observable
	_trackNumber = false;

	@computed
	get trackNumber () {
		return this._trackNumber;
	}

	set trackNumber (trackNumber) {
		this._trackNumber = trackNumber;
	}

	@observable
	_isExclusive = false;

	@computed
	get isExclusive () {
		return this._isExclusive;
	}

	set isExclusive (isExclusive) {
		this._isExclusive = isExclusive;
	}

	@observable
	_comment = false;

	@computed
	get comment () {
		return this._comment;
	}

	set comment (comment) {
		this._comment = comment;
	}

	@observable
	_isFullPreview = false;

	@computed
	get isFullPreview () {
		return this._isFullPreview;
	}

	set isFullPreview (isFullPreview) {
		this._isFullPreview = isFullPreview;
	}

	@observable
	_fullPreviewPosition = 0;

	@computed
	get fullPreviewPosition () {
		return this._fullPreviewPosition;
	}

	set fullPreviewPosition (fullPreviewPosition) {
		this._fullPreviewPosition = fullPreviewPosition;
	}

	@observable
	_plays = 0;

	@computed
	get plays () {
		return this._plays;
	}

	set plays (plays) {
		this._plays = plays;
	}

	@observable
	_hifives = 0;

	@computed
	get hifives () {
		return this._hifives;
	}

	set hifives (hifives) {
		this._hifives = hifives;
	}

	/** AI / pipeline — plain string or null (e.g. "Rock---Folk Rock") */
	@observable
	_genre = null;

	@computed
	get genre () {
		return this._genre;
	}

	set genre (genre) {
		this._genre = genre;
	}

	/** AI / pipeline — plain string or null */
	@observable
	_mood = null;

	@computed
	get mood () {
		return this._mood;
	}

	set mood (mood) {
		this._mood = mood;
	}
	
	@observable
	_status = 'pending';

	@computed
	get status () {
		return this._status;
	}

	set status (status) {
		this._status = status;
	}
	
	@observable
	_duration = 0;

	@computed
	get duration () {
		if(_.isString(this._duration) && this._duration.includes(':')) {
			return this._duration;
		}

		const duration = Math.round((parseFloat(this._duration) + Number.EPSILON) * 100) / 100;
		if (window.isNaN(duration) || duration === 0) {

			return 0;
		}

		
		// const seconds = duration % 60;
		var mins = Math.floor((duration / 60)).toFixed(0);
        var secs = Math.floor((duration % 60)).toFixed(0);
		return (mins + ":" + (secs < 10 ? "0" : "") + secs);

	}

	set duration (duration) {
		this._duration = duration;
	}

	@observable
	_active = true;

	@computed
	get active () {
		return this._active;
	}

	set active (active) {
		this._active = active;
	}

	@computed // @deprecated
	get path () {
		return `${config.paths.songBasePath}/${this.file}`;
	}

	@computed
	get downLoadPath () {
		return `${config.paths.songBasePath}/${this.downloadFile}`;
	}

	@computed
	get streamingPath () {
		return `${config.paths.songBasePath}/${this.streamFile}`;
	}

	@computed
	get previewingPath () {
		return `${config.paths.songBasePath}/${this.previewFile}`;
	}

	@computed
	get dateAdded () {
		return moment(new Date(this.createdAt), 'YYYYMMDD').fromNow();
	}

	@computed
	get showAlbumForm () {
		return this.albumID === 'new-album';
	}

	@computed
	get serialized () {
		return {
			_id: this._id,
			artistID: this.artistID,
			artist: this.artist,
			additionalArtists: this.additionalArtists,
			publisher: this.publisher,
			songWriters: this.songWriters,
			ISRC: this.ISRC,
			title: this.title,
			dashManifest: this.dashManifest,
			downloadFile: this.downloadFile,
			streamFile: this.streamFile,
			previewFile: this.previewFile,
			embedCode: this.embedCode,
			explicit: this.explicit,
			releaseDate: this.releaseDate,
			trackNumber: this.trackNumber,
			isExclusive: this.isExclusive,
			comment: this.comment,
			isFullPreview: this.isFullPreview,
			fullPreviewPosition: this.fullPreviewPosition,
			plays: this.plays,
			hifives: this.hifives,
			duration: this.duration,
			active: this.active,
			status: this.status,
			genre: this.genre,
			mood: this.mood
		};
	}

	/**
	 * Methods
	 */

	async delete () {
		await this.store.delete(this, {route: ''});
	}

	/**
	 * Actions
	 */

	@action
	async save (artist, opts = {}) {
		const data = await this.store.saveSong(this, opts);
		this.update(data);
	}

	@action
	async updateFullPreviewPosition (position) {
		await this.store.updateFullPreviewPosition(this, position);
	}

	@action
	async removeFullPreview () {
		const updated = await this.store.removeFullPreview(this);
		this.update(updated);
	}

	@action
	update ({
		_id,
		artistID,
		additionalArtists,
		publisher,
		songWriters,
		ISRC,
		title,
		dashManifest,
		downloadFile,
		streamFile,
		previewFile,
		embedCode,
		explicit,
		releaseDate,
		trackNumber,
		isExclusive,
		comment,
		isFullPreview,
		fullPreviewPosition,
		plays,
		hifives,
		duration,
		active,
		status,
		genre,
		mood
	}) {
		this._id = _.defaultTo(_id, this._id);
		this._artistID = _.defaultTo(artistID, this._artistID);
		this._additionalArtists = _.defaultTo(additionalArtists, this._additionalArtists);
		this._publisher = _.defaultTo(publisher, this._publisher);
		this._songWriters = _.defaultTo(songWriters, this._songWriters);
		this._ISRC = _.defaultTo(ISRC, this._ISRC);
		this._title = _.defaultTo(title, this._title);
		this._dashManifest = _.defaultTo(dashManifest, this._dashManifest);
		this._downloadFile = _.defaultTo(downloadFile, this._downloadFile);
		this._streamFile = _.defaultTo(streamFile, this._streamFile);
		this._previewFile = _.defaultTo(previewFile, this._previewFile);
		this._embedCode = _.defaultTo(embedCode, this._embedCode);
		this._explicit = _.defaultTo(explicit, this._explicit);
		this._releaseDate = _.defaultTo(releaseDate, this._releaseDate);
		this._trackNumber = _.defaultTo(trackNumber, this._trackNumber);
		this._isExclusive = _.defaultTo(isExclusive, this._isExclusive);
		this._comment = _.defaultTo(comment, this._comment);
		this._isFullPreview = _.defaultTo(isFullPreview, this._isFullPreview);
		this._fullPreviewPosition = _.defaultTo(fullPreviewPosition, this._fullPreviewPosition);
		this._plays = _.defaultTo(plays, this._plays);
		this._hifives = _.defaultTo(hifives, this._hifives);
		this._duration = _.defaultTo(duration, this._duration);
		this._active = _.defaultTo(active, this._active);
		this._status = _.defaultTo(status, this._status);
		if (genre !== undefined) {
			this._genre = genre;
		}
		if (mood !== undefined) {
			this._mood = mood;
		}
 	}

	/**
	 * Methods
	 */

	async hiFive (memberID) {
		if (_.isEmpty(memberID)) {

			console.error('No member ID provided');
			return;
		}

		try {

			const payload = {
				memberID,
				itemID: this.id,
				itemType: 'song'
			};

			const silent = true;
			const route = config.getRoute('hiFive');
			return await this.store.save(payload, { route, silent });
		} catch (err) {

			console.error('[ERROR: subscribing]', err);
		}
	}
}
